import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router';
import { NOT_FOUND_SEO, resolvePageSeo, routeOwnsItsSeo } from '@/config/seo';
import { env } from '@/config/env';
import { applySeo } from '@/utils/seo';

/**
 * Applies the metadata registry to the document on every navigation.
 *
 * Mounted once, above the routes. A `useLayoutEffect` rather than `useEffect`
 * so that a page which does own its metadata — `ArticleDetail`, whose title
 * comes from the fetched article — overrides this deterministically: React runs
 * every layout effect of a commit before any passive one, so the page's
 * `useSeo` always lands second regardless of mount order. That ordering used to
 * be luck, and `routeSeo.ts` existed to compensate for it.
 */
export function SeoHead() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    // The page sets its own; writing here first would only cause a flicker.
    if (routeOwnsItsSeo(pathname)) return;

    const entry = resolvePageSeo(pathname) ?? NOT_FOUND_SEO;

    applySeo({
      ...entry,
      path: pathname,
      /*
       * test.weareqa.com serves the same pages as production from a second
       * hostname. Without this the two compete as duplicates -- the canonical
       * alone is a hint, noindex is not.
       */
      forceNoindex: env.isStaging,
    });
  }, [pathname]);

  return null;
}
